import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SparklesIcon,
  XMarkIcon,
  ArrowPathIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentTextIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { filterProfileDiff, describeFilterDiff } from '../utils/execReportFilterProfile';
import {
  requestExecutiveReport,
  fetchExecutiveReports,
  fetchExecutiveReportStatus,
  fetchExecutiveReportContent,
} from '../api/client';
import { buildExecReportFactPack, countPackSubjects } from '../utils/execReportFactPack';
import { openReportInNewTab } from '../utils/storyGenerator';
import Badge from './ui/Badge';

const POLL_MS = 3000;
const CLIENT_TIMEOUT_MS = 8 * 60 * 1000;   // matches the worker's own budget

// The ONLY place the report-type vocabulary is mirrored client-side. The server owns it
// (its registry drives the template, prompt, schema, renderer, filename and audit labels);
// the client needs the id to send and the words to show.
//
// 🔴 A copy is not a duplicate contract only while it stays this small. Anything else about
// a type -- what it renders, what it costs, which template it needs -- belongs on the
// server, and `templateApprovedByType` in the listing response is how the client learns
// what it may offer.
export const REPORT_TYPES = {
  'executive-report': {
    title: 'Executive Report',
    // What the analyst is deciding to spend a provider call on.
    blurb: 'a multi-page, executive-level incident response report',
    filenameStem: 'Executive-Report',
  },
  'executive-brief': {
    title: 'Executive Brief',
    blurb: 'a one-page brief — how they got in, when they were active, when it was contained, '
      + 'and a checklist of what they did — with the supporting tables attached as addenda',
    filenameStem: 'Executive-Brief',
  },
};
const DEFAULT_TYPE = 'executive-report';

function statusTone(status) {
  switch (status) {
    case 'Completed': return 'success';
    case 'Failed':    return 'critical';
    case 'Cancelled': return 'neutral';
    case 'Running':   return 'info';
    default:          return 'warning';
  }
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function elapsedLabel(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Executive Report (AI) modal.
 *
 * Three jobs, in the order the analyst meets them:
 *   1. say what generation costs (an AI call, 1-4 minutes)
 *   2. state the SUBJECT CONSEQUENCE at the point of decision — an unmasked report
 *      is a durable artifact readable by anyone with case access, which the
 *      ephemeral per-analyst reveal toggle is not
 *   3. be the durable access path for reports generated earlier (report content
 *      requires the bearer-authed API; there are no anonymous URLs)
 */
export default function ExecutiveReportModal({
  open, onClose, reportParams, caseId, revealSubjects, filterState, onApplyReportView,
  reportType = DEFAULT_TYPE,
  // 🔴 GENERATE IS A PERSISTENCE BOUNDARY. Phishing flags and URL classifications are
  // saved on a 1-SECOND DEBOUNCE, and `RequestExecutiveReport` rebuilds the analyst's
  // judgments from the Cases ENTITY and discards the client's copies. So a Generate
  // clicked inside that window loses the entry-point finding SERVER-SIDE no matter what
  // the pack says — the flag exists in React state and not yet in storage. This awaits the
  // flush first, and surfaces a failed save in the modal rather than the console.
  onBeforeGenerate = null,
}) {
  const type = REPORT_TYPES[reportType] ? reportType : DEFAULT_TYPE;
  const typeInfo = REPORT_TYPES[type];
  const [loadingList, setLoadingList] = useState(false);
  const [listing, setListing] = useState(null);      // { reports, templateApproved, mspCode, featureEnabled }
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);    // { reportId, status, startedAt }
  const [now, setNow] = useState(Date.now());
  const [openingId, setOpeningId] = useState(null);

  const pollRef = useRef(null);
  const cancelledRef = useRef(false);
  // The active generation job, held across a close/reopen. The modal stays mounted
  // (open just toggles), so closing it mid-generation tore down the poll loop but left
  // `generating`/`progress` set — reopening then showed a permanent "Generating… · Queued"
  // over a job that had already finished (its completed row is right there in the list).
  // This lets reopen RECONCILE: resume the poll for a live job, settle a finished one.
  const inFlightRef = useRef(null);   // { reportId, startedAt } | null

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      setListing(await fetchExecutiveReports(caseId));
    } catch (err) {
      setError(err.message || 'Could not load prior reports.');
    } finally {
      setLoadingList(false);
    }
  }, [caseId]);

  // Drives the elapsed timer only.
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [generating]);

  const openReport = useCallback(async (reportId, openedRowType) => {
    setOpeningId(reportId);
    setError(null);
    try {
      const html = await fetchExecutiveReportContent(caseId, reportId);
      // The filename follows the ROW's type, not this modal's -- the prior-reports list
      // shows documents of both types and a Brief must not be saved as ...-Executive-Report.html.
      const rowType = REPORT_TYPES[openedRowType] ? openedRowType : type;
      // Reuses the CSP-safe viewer: the child tab inherits the app CSP
      // (script-src 'self'), so its export buttons are wired from THIS window.
      openReportInNewTab(html, `${caseId}-${REPORT_TYPES[rowType].filenameStem}.html`);
    } catch (err) {
      setError(err.message || 'Could not open the report.');
    } finally {
      setOpeningId(null);
    }
  }, [caseId, type]);

  // autoOpen: open the finished report in a new tab on completion. True for a job the
  // analyst just started (the poll runs inside their click's async chain); false when
  // RESUMING a poll on reopen, where opening a tab is no longer a user gesture and would
  // be popup-blocked — the completed row + its View button is the path there.
  const pollUntilDone = useCallback((reportId, startedAt, autoOpen = true) => {
    const tick = async () => {
      if (cancelledRef.current) return;
      try {
        const status = await fetchExecutiveReportStatus(caseId, reportId);
        setProgress({ reportId, status: status.status, startedAt });

        if (status.status === 'Completed') {
          inFlightRef.current = null;
          setGenerating(false);
          await loadList();
          if (autoOpen) await openReport(reportId, type);
          return;
        }
        if (status.status === 'Failed' || status.status === 'Cancelled') {
          inFlightRef.current = null;
          setGenerating(false);
          setError(status.error || `Report generation ${status.status.toLowerCase()} (${status.errorCode || 'unknown'}).`);
          await loadList();
          return;
        }
      } catch (err) {
        inFlightRef.current = null;
        setGenerating(false);
        setError(err.message || 'Lost contact with the generation job.');
        return;
      }

      if (Date.now() - startedAt > CLIENT_TIMEOUT_MS) {
        // The job is still running server-side; the list is the durable path to it.
        inFlightRef.current = null;
        setGenerating(false);
        setError('Still running — this is taking longer than usual. Close this and check the list in a few minutes.');
        await loadList();
        return;
      }
      pollRef.current = setTimeout(tick, POLL_MS);
    };
    pollRef.current = setTimeout(tick, POLL_MS);
  }, [caseId, loadList, openReport, type]);

  // 🔴 THIS EFFECT MUST STAY BELOW `pollUntilDone`, and that is not a style preference.
  // It sat ABOVE it when the reconcile landed in v1.176.1, and a dependency array is
  // evaluated DURING RENDER -- so `[open, loadList, pollUntilDone]` read a `const` that
  // had not initialized yet and threw `ReferenceError: Cannot access 'pollUntilDone'
  // before initialization`. The modal renders unconditionally as part of CaseDetail, so
  // that took out the WHOLE case page: a blank screen on every case, not a broken dialog.
  // React's own linting would have caught it; the build does not.
  useEffect(() => {
    if (!open) return undefined;
    cancelledRef.current = false;
    loadList();
    // Reconcile any job left in flight by a previous open of this same (mounted) modal.
    // A live job resumes polling (autoOpen off — see pollUntilDone); a finished one is
    // settled by that poll's terminal branch. Without this, a job that completed while
    // the modal was closed left `generating` stuck true forever — a permanent
    // "Generating…" that also disabled the Generate button so no NEW report could start.
    if (inFlightRef.current) {
      setGenerating(true);
      pollUntilDone(inFlightRef.current.reportId, inFlightRef.current.startedAt, false);
    } else {
      // No active job — clear any transient state a prior open may have left behind.
      setGenerating(false);
      setProgress(null);
    }
    return () => {
      cancelledRef.current = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [open, loadList, pollUntilDone]);

  const handleGenerate = useCallback(async () => {
    setError(null);
    setGenerating(true);
    const startedAt = Date.now();
    setProgress({ reportId: null, status: 'Building report data…', startedAt });
    try {
      // Flush the debounced case judgments and WAIT. A failure here stops generation:
      // producing a report the server will build from a stale entity is worse than not
      // producing one, because the missing finding is invisible in the result.
      if (onBeforeGenerate) {
        setProgress({ reportId: null, status: 'Saving case findings…', startedAt });
        await onBeforeGenerate();
        setProgress({ reportId: null, status: 'Building report data…', startedAt });
      }
      const factPack = buildExecReportFactPack(reportParams);
      const res = await requestExecutiveReport(caseId, factPack, type);
      inFlightRef.current = { reportId: res.reportId, startedAt };
      setProgress({ reportId: res.reportId, status: 'Queued', startedAt });
      pollUntilDone(res.reportId, startedAt);
    } catch (err) {
      setGenerating(false);
      const code = err.body?.code;
      if (code === 'facts_reconcile_failed') {
        // Show the server's actual reasons. The 422 body has always carried `errors`,
        // and discarding them cost a round-trip through App Insights to find out that
        // log_types_reviewed was speaking the wrong vocabulary — while the message
        // told the analyst to reload, which could never have fixed it. Reconciliation
        // rejects a stale pack AND a contract bug, and only the first is the
        // analyst's to act on, so show which one this is.
        const reasons = Array.isArray(err.body?.errors) ? err.body.errors : [];
        setError(
          reasons.length
            ? `The report data does not match this case's investigation results:\n• ${reasons.join('\n• ')}`
            : "The report data no longer matches this case's investigation results — reload the case and try again.",
        );
      } else if (code === 'already_running') {
        // The server names WHICH type is running: only one job runs per case across both
        // types (they share a provider rate limit), so an analyst asking for a Brief can be
        // blocked by a Report and needs to know that is what they are waiting for.
        setError(err.message || 'A document is already being generated for this case. Wait for it to finish, then try again.');
        loadList();
      } else if (code === 'template_not_approved') {
        setError(err.message);
      } else if (code === 'case_save_failed') {
        setError(err.message);
      } else {
        setError(err.message || 'Could not start report generation.');
      }
    }
  }, [caseId, reportParams, loadList, pollUntilDone, type, onBeforeGenerate]);

  if (!open) return null;

  const reports = listing?.reports || [];
  // PER TYPE. `templateApproved` is the Report's answer and is kept for older payloads;
  // an MSP can hold one template and not the other, and offering a button that is
  // guaranteed to 422 is the failure this listing exists to prevent.
  const templateApproved = listing
    ? (listing.templateApprovedByType?.[type] ?? (type === DEFAULT_TYPE ? listing.templateApproved : false))
    : undefined;
  const featureEnabled = listing?.featureEnabled !== false;

  // Every metric in the report is computed from the analyst's curated view, so two
  // reports from one run could carry different numbers with nothing recorded to explain
  // it. The report therefore requires a FIXED filter state. The button is disabled until
  // it is in force — but never silently: the wrong toggles are named and one click sets
  // them (memory feedback_ui_open_api_gated_silent_button).
  const wrongFilters = filterProfileDiff(filterState);
  const filtersReady = wrongFilters.length === 0;
  const canGenerate = !generating && templateApproved && featureEnabled && filtersReady;

  // Counting from the pack (not the raw results) is what actually ships, so the
  // number quoted here is the number that lands in the artifact.
  let subjectCount = 0;
  if (revealSubjects) {
    try { subjectCount = countPackSubjects(buildExecReportFactPack(reportParams)); } catch { subjectCount = 0; }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-700 px-5 py-3">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-100">
            <SparklesIcon className="h-5 w-5 text-purple-400" />
            {typeInfo.title}
          </h2>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-200">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-gray-300">
            Turns this investigation into {typeInfo.blurb}, {listing?.mspCode ? `${listing.mspCode}-branded` : 'MSP-branded'}.
            {type === 'executive-brief'
              ? ' An AI writing assistant drafts only the bottom line; every other statement, figure, timestamp, address and subject line on the page is rendered from the recorded findings.'
              : ' An AI writing assistant drafts the narrative from the recorded findings; every figure, timestamp, address and subject line is rendered from those findings, not written by the model.'}
            {' '}Generation takes about <strong>1–4 minutes</strong>.
          </p>

          {/* Subject state — the consequence is stated at the point of decision, not after. */}
          {revealSubjects ? (
            <div className="rounded border border-amber-700/60 bg-amber-900/20 px-3 py-2 text-sm text-amber-200">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">Subjects: revealed</div>
                  <div className="mt-0.5">
                    {subjectCount > 0 ? `${subjectCount} verbatim subject line${subjectCount === 1 ? '' : 's'} will be` : 'Verbatim subject lines will be'} written
                    into this report and will stay readable to anyone with access to this case. Each time it is opened is logged.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded border border-gray-600 bg-gray-900/40 px-3 py-2 text-sm text-gray-300">
              <span className="font-semibold">Subjects: masked</span> — the report will describe mailbox access by folder and
              count, and will omit subject lines.
            </div>
          )}

          {listing && !templateApproved && (
            <div className="rounded border border-gray-600 bg-gray-900/40 px-3 py-2 text-sm text-gray-300">
              This needs an approved {listing.mspCode || 'MSP'} {typeInfo.title} template. Ask an administrator to add one.
            </div>
          )}
          {listing && templateApproved && !featureEnabled && (
            <div className="rounded border border-gray-600 bg-gray-900/40 px-3 py-2 text-sm text-gray-300">
              Generation is temporarily disabled. Documents already generated can still be opened.
            </div>
          )}

          {/* whitespace-pre-line so multi-reason reconcile errors render as a
              bullet list rather than one run-on line */}
          {error && (
            <div className="whitespace-pre-line rounded border border-red-700/60 bg-red-900/20 px-3 py-2 text-sm text-red-200">{error}</div>
          )}

          {/* The filter gate. Named toggles + one click to fix, never a dead button. */}
          {filterState && !filtersReady && (
            <div className="rounded border border-amber-700/60 bg-amber-900/20 px-3 py-2 text-sm text-amber-200">
              <div className="flex items-start gap-2">
                <FunnelIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold">The case view is not the report view</div>
                  <div className="mt-0.5">
                    {typeInfo.title === 'Executive Brief' ? 'A brief' : 'An executive report'} is built from a fixed set of filters, so its figures mean the same thing on
                    every case and every re-run. It needs {wrongFilters.length === 1 ? 'this setting' : `these ${wrongFilters.length} settings`},
                    which {wrongFilters.length === 1 ? 'is' : 'are'} not in force yet:
                  </div>
                  {/* Each row states the TARGET and the CURRENT state. The labels are the
                      filter pills' own engaged labels, so they are state-phrased ("Risk
                      detections hidden") — listing them alone under "not set for
                      reporting" reads both ways and the two readings are opposites. An
                      operator read it as the report wanting risk detections SHOWN. */}
                  <ul className="mt-1.5 space-y-1">
                    {wrongFilters.map((f) => {
                      const d = describeFilterDiff(f);
                      return (
                        <li key={f.key} className="flex items-baseline gap-2 text-xs">
                          <span className="rounded bg-amber-800/40 px-1.5 py-0.5">{d.label}</span>
                          <span className="text-amber-300/70">currently {d.now}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    onClick={onApplyReportView}
                    disabled={!onApplyReportView}
                    className="btn-secondary mt-2.5 flex items-center gap-1.5 py-1 text-xs disabled:opacity-50"
                  >
                    <FunnelIcon className="h-3.5 w-3.5" />
                    Set the report view
                  </button>
                  <div className="mt-1.5 text-xs text-amber-300/80">
                    This changes what the case shows too — that is the point, so the report describes the view you
                    reviewed. You can set any filter back afterwards.
                  </div>
                </div>
              </div>
            </div>
          )}
          {filterState && filtersReady && (
            <div className="rounded border border-gray-600 bg-gray-900/40 px-3 py-2 text-sm text-gray-300">
              <span className="font-semibold text-green-300">Report view is set</span> — all case filters are in their
              reporting state, so this report&apos;s figures are reproducible.
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="btn-primary flex items-center gap-2 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
            >
              {generating ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <SparklesIcon className="h-4 w-4" />}
              {generating ? 'Generating…' : `Generate ${typeInfo.title.toLowerCase()}`}
            </button>
            {generating && progress && (
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <ClockIcon className="h-3.5 w-3.5" />
                {progress.status} · {elapsedLabel(now - progress.startedAt)}
              </span>
            )}
          </div>

          {/* Prior reports — the durable access path. */}
          <div className="border-t border-gray-700 pt-4">
            <div className="mb-2 flex items-center justify-between">
              {/* BOTH types, deliberately: they are two documents about one case and an
                  analyst looking for "the one I generated" should not have to guess which
                  modal it is behind. Each row is labelled with its own type. */}
              <h3 className="text-sm font-semibold text-gray-200">Prior documents</h3>
              <button
                onClick={loadList}
                disabled={loadingList}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-3.5 w-3.5 ${loadingList ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadingList && !listing && <div className="py-4 text-center text-sm text-gray-400">Loading…</div>}
            {listing && reports.length === 0 && (
              <div className="py-4 text-center text-sm text-gray-500">Nothing has been generated for this case yet.</div>
            )}

            {reports.length > 0 && (
              <ul className="divide-y divide-gray-700 rounded border border-gray-700">
                {reports.map((r) => (
                  <li key={r.reportId} className="flex items-start justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                        {/* The type is on the row, not inferred from the open modal: a row
                            written before the column existed reads as the Report, which is
                            what it is. */}
                        <Badge tone={(r.reportType || DEFAULT_TYPE) === 'executive-brief' ? 'purple' : 'info'}>
                          {REPORT_TYPES[r.reportType || DEFAULT_TYPE]?.title || r.reportType}
                        </Badge>
                        {r.severity && <span className="text-sm font-medium text-gray-200">{r.severity}</span>}
                        {/* A report generated by one analyst is otherwise
                            indistinguishable from a masked one, so the badge goes
                            where a reader sees it BEFORE opening. */}
                        {r.containsSubjects && (
                          <Badge tone="warning" dot>contains email subjects</Badge>
                        )}
                      </div>
                      {r.headline && <div className="mt-1 truncate text-sm text-gray-300">{r.headline}</div>}
                      <div className="mt-1 text-xs text-gray-500">
                        {r.requestedBy || 'unknown'} · {formatWhen(r.completedUtc || r.requestedUtc)}
                        {r.model ? ` · ${r.model}` : ''}
                        {r.factsTruncated ? ` · ${r.omittedCount} item(s) omitted for size` : ''}
                      </div>
                      {r.status === 'Failed' && r.error && (
                        <div className="mt-1 text-xs text-red-300">{r.error}</div>
                      )}
                    </div>
                    {r.status === 'Completed' && (
                      <button
                        onClick={() => openReport(r.reportId, r.reportType || DEFAULT_TYPE)}
                        disabled={openingId === r.reportId}
                        className="btn-secondary flex flex-shrink-0 items-center gap-1.5 py-1 text-xs disabled:opacity-50"
                      >
                        {openingId === r.reportId
                          ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                          : <EyeIcon className="h-3.5 w-3.5" />}
                        View
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-start gap-2 border-t border-gray-700 pt-3 text-xs text-gray-500">
            <DocumentTextIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Each document records which investigation run it came from, the filters in effect, and the
              template and model versions used. Only one can be generated at a time per case.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
